// /src/pages/Register.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null); // { email, role }
  const [tokenError, setTokenError] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  // Read token from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setTokenError("No invite token found. Please use the link from your invitation email.");
      setTokenLoading(false);
      return;
    }
    setToken(t);

    // Validate the token with the API
    fetch(`/api/Register?token=${t}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setTokenError(data.error);
        } else {
          setInviteInfo(data);
        }
        setTokenLoading(false);
      })
      .catch(() => {
        setTokenError("Failed to validate invite link. Please try again.");
        setTokenLoading(false);
      });
  }, []);

  const passwordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const labels = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];
    const colors = ["", "#ef4444", "#f97316", "#eab308", "#22c55e", "#16a34a"];
    return { score, label: labels[score], color: colors[score] };
  };

  const strength = passwordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!fullName.trim()) return setFormError("Please enter your full name.");
    if (password.length < 8) return setFormError("Password must be at least 8 characters.");
    if (password !== confirmPassword) return setFormError("Passwords do not match.");

    setSubmitting(true);
    try {
      const res = await fetch("/api/Register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, full_name: fullName.trim() }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setFormError(data.error || "Registration failed. Please try again.");
        setSubmitting(false);
        return;
      }

      // Save session token and user info
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setSuccess(true);

      // Redirect to dashboard after brief delay
      setTimeout(() => navigate("/"), 2000);
    } catch {
      setFormError("Network error. Please try again.");
      setSubmitting(false);
    }
  };

  // ─── Loading state ───────────────────────────────────────────────────────
  if (tokenLoading) {
    return (
      <div className="register-page">
        <div className="register-card">
          <div className="register-loading">
            <Loader size={28} className="spin" />
            <p>Validating your invite...</p>
          </div>
        </div>
        <RegisterStyles />
      </div>
    );
  }

  // ─── Token error ─────────────────────────────────────────────────────────
  if (tokenError) {
    return (
      <div className="register-page">
        <div className="register-card">
          <div className="register-error-state">
            <AlertCircle size={40} color="#ef4444" />
            <h2>Invalid Invite Link</h2>
            <p>{tokenError}</p>
          </div>
        </div>
        <RegisterStyles />
      </div>
    );
  }

  // ─── Success ─────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="register-page">
        <div className="register-card">
          <div className="register-success-state">
            <CheckCircle size={48} color="#16a34a" />
            <h2>Welcome aboard!</h2>
            <p>Your account is set up. Redirecting you to the dashboard...</p>
          </div>
        </div>
        <RegisterStyles />
      </div>
    );
  }

  // ─── Registration form ───────────────────────────────────────────────────
  return (
    <div className="register-page">
      <div className="register-card">
        <div className="register-header">
          <h1>Create your account</h1>
          <p className="register-subtitle">
            You've been invited as a{" "}
            <span className="register-role">{inviteInfo?.role?.replace("_", " ")}</span>
          </p>
          <p className="register-email">{inviteInfo?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              className="form-input"
              type="text"
              placeholder="Jane Smith"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="password-wrapper">
              <input
                className="form-input"
                type={showPassword ? "text" : "password"}
                placeholder="Min. 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {password && (
              <div className="password-strength">
                <div className="strength-bars">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className="strength-bar"
                      style={{ background: i <= strength.score ? strength.color : "#e2e8f0" }}
                    />
                  ))}
                </div>
                <span className="strength-label" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              className="form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="field-error">Passwords don't match</p>
            )}
          </div>

          {formError && (
            <div className="form-error-banner">
              <AlertCircle size={14} />
              {formError}
            </div>
          )}

          <button
            className="btn btn-primary register-submit"
            type="submit"
            disabled={submitting}
          >
            {submitting ? (
              <><Loader size={15} className="spin" /> Creating account...</>
            ) : (
              "Create Account →"
            )}
          </button>
        </form>
      </div>

      <RegisterStyles />
    </div>
  );
}

function RegisterStyles() {
  return (
    <style>{`
      .register-page {
        min-height: 100vh;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
      }
      .register-card {
        background: #fff;
        border-radius: 16px;
        padding: 40px;
        width: 100%;
        max-width: 440px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      }
      .register-header {
        margin-bottom: 28px;
      }
      .register-header h1 {
        font-size: 24px;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 8px;
      }
      .register-subtitle {
        color: #64748b;
        font-size: 14px;
        margin: 0 0 4px;
      }
      .register-role {
        text-transform: capitalize;
        font-weight: 600;
        color: #0f172a;
      }
      .register-email {
        color: #94a3b8;
        font-size: 13px;
        margin: 0;
      }
      .register-form {
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .password-wrapper {
        position: relative;
      }
      .password-wrapper .form-input {
        padding-right: 40px;
        width: 100%;
        box-sizing: border-box;
      }
      .password-toggle {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        cursor: pointer;
        color: #94a3b8;
        padding: 4px;
        display: flex;
        align-items: center;
      }
      .password-toggle:hover { color: #475569; }
      .password-strength {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
      }
      .strength-bars {
        display: flex;
        gap: 4px;
        flex: 1;
      }
      .strength-bar {
        height: 4px;
        border-radius: 2px;
        flex: 1;
        transition: background 0.2s;
      }
      .strength-label {
        font-size: 12px;
        font-weight: 500;
        min-width: 70px;
        text-align: right;
      }
      .field-error {
        color: #ef4444;
        font-size: 12px;
        margin: 4px 0 0;
      }
      .form-error-banner {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .register-submit {
        width: 100%;
        justify-content: center;
        padding: 14px;
        font-size: 15px;
        font-weight: 600;
        gap: 8px;
      }
      .register-loading, .register-error-state, .register-success-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 24px 0;
        text-align: center;
      }
      .register-error-state h2, .register-success-state h2 {
        font-size: 20px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
      }
      .register-error-state p, .register-success-state p, .register-loading p {
        color: #64748b;
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      .spin { animation: spin 0.8s linear infinite; }
    `}</style>
  );
}